export interface Configuration {
  apiUrl: string;
}

export interface DeviceList {
  totalRows: number;
  items: Array<{
    family: string;
    uid: string;
    model: string;
    firmware: string;
  }>;
}

export interface LiveLink {
  playURL: string;
  expiredIn: number;
}

export interface BatteryState {
  percent: number;
  chargeStatus: string;
}

export async function getConfiguration(signal?: AbortSignal) {
  let response: Response | undefined = undefined;

  try {
    response = await fetch("config.json", {
      signal: signal,
    });
  } catch (error) {
    if ((error as Error)?.name !== "AbortError") {
      throw error;
    }

    return;
  }

  if (response.ok) {
    return await (response.json() as Promise<Configuration | undefined>);
  } else {
    throw new Error(response.statusText);
  }
}

export async function listServers(apiUrl: string, signal?: AbortSignal) {
  let response: Response | undefined = undefined;

  try {
    response = await fetch(`${apiUrl}/server`, {
      signal: signal,
    });
  } catch (error) {
    if ((error as Error)?.name !== "AbortError") {
      throw error;
    }

    return;
  }

  if (response.ok) {
    return await (response.json() as Promise<Array<string> | undefined>);
  } else {
    throw new Error(response.statusText);
  }
}

export async function listDevices(
  apiUrl: string,
  server: string,
  signal?: AbortSignal
) {
  let response: Response | undefined = undefined;

  try {
    response = await fetch(`${apiUrl}/device/${server}`, {
      signal: signal,
    });
  } catch (error) {
    if ((error as Error)?.name !== "AbortError") {
      throw error;
    }

    return;
  }

  if (response.ok) {
    return await (response.json() as Promise<DeviceList | undefined>);
  } else {
    throw new Error(response.statusText);
  }
}

export async function getLive(
  apiUrl: string,
  server: string,
  uid: string,
  signal?: AbortSignal
) {
  let response: Response | undefined = undefined;

  try {
    response = await fetch(`${apiUrl}/device/${server}/${uid}/rtsp-url`, {
      signal: signal,
    });
  } catch (error) {
    if ((error as Error)?.name !== "AbortError") {
      throw error;
    }

    return;
  }

  if (response.ok) {
    return await (response.json() as Promise<LiveLink | undefined>);
  } else {
    throw new Error(response.statusText);
  }
}

export async function getBatteryState(
  apiUrl: string,
  server: string,
  uid: string,
  signal?: AbortSignal
) {
  let response: Response | undefined = undefined;

  try {
    response = await fetch(`${apiUrl}/device/${server}/${uid}/battery-state`, {
      signal: signal,
    });
  } catch (error) {
    if ((error as Error)?.name !== "AbortError") {
      throw error;
    }

    return;
  }

  if (response.ok) {
    return await (response.json() as Promise<BatteryState | undefined>);
  } else {
    throw new Error(response.statusText);
  }
}

export async function getMjpegChunks(
  apiUrl: string,
  server: string,
  uid: string,
  mediaType: "rtsp" | "http",
  signal?: AbortSignal
) {
  let response: Response | undefined = undefined;

  try {
    response = await fetch(
      `${apiUrl}/media/${server}/${uid}/mjpeg/${mediaType}`,
      {
        signal: signal,
      }
    );
  } catch (error) {
    if ((error as Error)?.name !== "AbortError") {
      throw error;
    }

    return;
  }

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  if (!response.body) {
    return;
  }

  const boundaryMatch = response.headers
    .get("Content-Type")
    ?.match(
      new RegExp("^multipart/x-mixed-replace; boundary=([^\\s;]+)$", "im")
    );
  if (boundaryMatch && boundaryMatch[1]) {
    return { stream: response.body, boundary: boundaryMatch[1] };
  }
}
